/**
 * SOLID Review
 * S — Strong. Sole job: implement IAdapterApolloLiveKit's signaling/admin operations against the real
 *     backend via Apollo's imperative client API. Extracted from ApolloRoomAdapter.ts (see
 *     plan/plan_frontend_makeover.md) — these 5 operations are LiveKit concerns that happened to be
 *     co-located on the room adapter at the time each was added, not room-lifecycle operations themselves.
 *     Every method here is a verbatim move of already-correct, already-tested logic, not new code.
 * O — Good. A new LiveKit signaling/admin operation is a new method here plus a new IAdapterApolloLiveKit
 *     signature — mechanical, no existing method touched.
 * L — N/A — no inheritance hierarchy; implements IAdapterApolloLiveKit directly.
 * I — Strong. Implements exactly the narrow LiveKit-signaling interface it needs, nothing else — no longer
 *     bundled alongside room-lifecycle operations on a shared port.
 * D — Strong. Takes its domain's GraphQL port, LiveKitTokenService and the logger as constructor-injected dependencies (same
 *     pattern ApolloRoomAdapter.ts itself established, since getLiveKitToken delegates to
 *     LiveKitTokenService the same way it always did) rather than importing singletons — testable without
 *     a real network. Its calls go through `GraphQLCall`, which answers the port's failure (§28.288).
 */
import { gql } from '@apollo/client';
import type { Result } from '../../shared/Result';
import { ok } from '../../shared/Result';
import type { RoomId, PlayerId } from '../../application/ports/ClientVocabulary';
import type { IEntityLiveKitToken, IEntityLiveKitHealth, IEntityLiveKitUsageReport } from '../../application/livekit/ports/IAdapterApolloLiveKit';
import type { IAdapterApolloLiveKit } from '../../application/livekit/ports/IAdapterApolloLiveKit';
import type { LiveKitTokenService } from './LiveKitTokenService';
import type { IPortGraphQLDomainClient } from '../contract/ports';
import type { IServiceLogger } from '../platform/IServiceLogger';
import { GraphQLCall } from '../graphql/GraphQLCall';

const MARK_MEDIA_PRESENCE_MUTATION = gql`
  mutation ApolloLiveKitAdapter_MarkMediaPresence($roomId: ID!, $usedVideo: Boolean!, $usedAudio: Boolean!) {
    markMediaPresence(roomId: $roomId, usedVideo: $usedVideo, usedAudio: $usedAudio)
  }
`;

const REPORT_LIVEKIT_CONNECTION_STATE_MUTATION = gql`
  mutation ApolloLiveKitAdapter_ReportLiveKitConnectionState($roomId: ID!, $playerId: ID!, $connected: Boolean!) {
    reportLiveKitConnectionState(roomId: $roomId, playerId: $playerId, connected: $connected)
  }
`;

const LIVEKIT_HEALTH_QUERY = gql`
  query ApolloLiveKitAdapter_LiveKitHealth($playerId: ID!) {
    liveKitHealth(playerId: $playerId) {
      configured activeRooms activeParticipants
    }
  }
`;

const LIVEKIT_USAGE_REPORT_QUERY = gql`
  query ApolloLiveKitAdapter_LiveKitUsageReport($playerId: ID!, $startDate: String!, $endDate: String!) {
    liveKitUsageReport(playerId: $playerId, startDate: $startDate, endDate: $endDate) {
      totalMinutes
      entries { date playerId minutes }
    }
  }
`;

export class ApolloLiveKitAdapter implements IAdapterApolloLiveKit {
  private readonly call: GraphQLCall;

  // The executor arrives from this domain's socket on ComponentFrontendInfrastructure, not as
  // the raw ApolloClient. That socket is the single path from "this app can reach the domain"
  // to "this adapter executes" — disconnect it and this adapter degrades to the Null client
  // instead of quietly using a module singleton (§28.72).
  constructor(
    client: IPortGraphQLDomainClient,
    private readonly liveKitTokenService: LiveKitTokenService,
    logger: IServiceLogger,
  ) {
    this.call = new GraphQLCall('ApolloLiveKitAdapter', client, logger);
  }

  async getLiveKitToken(roomId: RoomId, playerId: PlayerId): Promise<Result<IEntityLiveKitToken>> {
    const result = await this.liveKitTokenService.getToken(roomId, playerId);
    if (!result.success) return result;
    const { token, serverUrl } = result.value;
    const liveKitTokenResult: IEntityLiveKitToken = { token, serverUrl };
    return ok(liveKitTokenResult);
  }

  async markMediaPresence(roomId: RoomId, usedVideo: boolean, usedAudio: boolean): Promise<Result<void>> {
    return this.call.done('markMediaPresence', 'marking media presence', (apollo) => apollo.mutate({
      mutation: MARK_MEDIA_PRESENCE_MUTATION,
      variables: { roomId, usedVideo, usedAudio },
    }));
  }

  async reportLiveKitConnectionState(roomId: RoomId, playerId: PlayerId, connected: boolean): Promise<Result<void>> {
    return this.call.done('reportLiveKitConnectionState', 'reporting LiveKit connection state', (apollo) => apollo.mutate({
      mutation: REPORT_LIVEKIT_CONNECTION_STATE_MUTATION,
      variables: { roomId, playerId, connected },
    }));
  }

  async getLiveKitHealth(playerId: PlayerId): Promise<Result<IEntityLiveKitHealth>> {
    return this.call.required('getLiveKitHealth', 'fetching LiveKit health', (apollo) => apollo.query<{ liveKitHealth: IEntityLiveKitHealth }>({
      query: LIVEKIT_HEALTH_QUERY,
      variables: { playerId },
      fetchPolicy: 'network-only',
    }), (data) => data.liveKitHealth);
  }

  async getLiveKitUsageReport(playerId: PlayerId, startDate: string, endDate: string): Promise<Result<IEntityLiveKitUsageReport>> {
    return this.call.required('getLiveKitUsageReport', 'fetching LiveKit usage report', (apollo) => apollo.query<{ liveKitUsageReport: IEntityLiveKitUsageReport }>({
      query: LIVEKIT_USAGE_REPORT_QUERY,
      variables: { playerId, startDate, endDate },
      fetchPolicy: 'network-only',
    }), (data) => data.liveKitUsageReport);
  }
}
